import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';
import { ParcelItem } from '../../../models/hsCode';
import {
  ActionIcons,
  EditButton,
  RemoveButton,
  ParcelInfo,
  ParcelItemContainer,
  ParcelItemHeaderContainer,
  ParcelItemHeader,
  FilledWithAiTag,
  ParcelItemPropName,
  ParcelItemPropValue,
  ParcelProps,
  ParcelItemWrapper,
} from './parcel-items-management.styles';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/delete-icon.svg';
import { ReactComponent as FilledWithAiSparkleIcon } from './filled-with-ai-sparkle.svg';
import ParcelItemEditForm from './parcel-item-form.component';
import { getFormattedPrice } from '../../../utils/price/get-formatted-price';
import { useModal } from '../../../common/modal';
import { Modals } from '../../../constants/modals';
import { useStore } from '../../../stores/store';
import CustomSkeleton from '../../../common/skeletons/custom-skeleton-component';
import { meest } from '../../../styles/meest';
import Separator from '../../../common/separator/separator.component';
import { useEditForm } from './edit-form-manager';
import { getAdditionalFieldTransKey } from './configs/additional-fields.config';

interface Props {
  item?: ParcelItem;
  withSeparator?: boolean;
  currencySymbol: string;
  shouldShowDeleteButton?: boolean;
  disableActions?: boolean;
}

const ParcelItemComponent = ({
  item,
  withSeparator,
  currencySymbol,
  shouldShowDeleteButton,
  disableActions = false,
}: Props) => {
  const {
    localizationsStore: { isLanguageChanging },
    parcelCreationStore: { deleteItemInShipment },
  } = useStore();
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  const { openedItemId, openEditForm } = useEditForm();

  const isEditing = openedItemId === item?.id;

  const handleDeleteItem = () => {
    if (item) {
      deleteItemInShipment(item.id);
    }
    closeModal(Modals.DELETE_ITEM);
  };

  const showDeleteItemModal = () => {
    openModal({
      id: Modals.DELETE_ITEM,
      name: Modals.DELETE_ITEM,
      props: {
        onApprove: handleDeleteItem,
      },
    });
  };

  const showItem = !isEditing && !!item;

  return (
    <ParcelItemWrapper>
      {showItem && (
        <ParcelItemContainer>
          <ParcelInfo>
            {isLanguageChanging ? (
              <CustomSkeleton
                display='grid'
                height='2.2rem'
                pb='1.2rem'
              />
            ) : (
              <ParcelItemHeaderContainer>
                <ParcelItemHeader>{item.description_standart}</ParcelItemHeader>
                {item.is_ai_generated && (
                  <FilledWithAiTag>
                    <span
                      className='filled-with-ai-icon'
                      aria-hidden='true'>
                      <FilledWithAiSparkleIcon />
                    </span>
                    {t('parcel_item_filled_with_ai')}
                  </FilledWithAiTag>
                )}
              </ParcelItemHeaderContainer>
            )}
            <ParcelProps>
              <ParcelItemPropName>{t('quantity')}</ParcelItemPropName>
              <ParcelItemPropValue>{item.number_of_items}</ParcelItemPropValue>
              <ParcelItemPropName>{t('declared_value')}</ParcelItemPropName>
              <ParcelItemPropValue>{`${getFormattedPrice(item?.value, currencySymbol)}`}</ParcelItemPropValue>
              {item.additional_fields &&
                Object.entries(item.additional_fields)
                  .filter(([, fieldValue]) => !!fieldValue?.at(0))
                  .map(([fieldName, fieldValue]) => (
                    <Fragment key={`${fieldName}_${fieldValue}`}>
                      <ParcelItemPropName>
                        {t(getAdditionalFieldTransKey(fieldName))}
                      </ParcelItemPropName>
                      <ParcelItemPropValue>
                        {fieldValue.at(0)}
                      </ParcelItemPropValue>
                    </Fragment>
                  ))}
              {item?.description_free && (
                <>
                  <ParcelItemPropName>
                    {t('parcel_content_description_label')}
                  </ParcelItemPropName>
                  <ParcelItemPropValue>
                    {item?.description_free}
                  </ParcelItemPropValue>
                </>
              )}
              <ParcelItemPropName>{t('conditions')}</ParcelItemPropName>
              <ParcelItemPropValue>
                {t(item.is_new === false ? 'used' : 'new')}
              </ParcelItemPropValue>
            </ParcelProps>
          </ParcelInfo>
          <ActionIcons>
            <EditButton
              onClick={() => openEditForm(item.id)}
              theme={meest}
              disabled={isLanguageChanging || disableActions}>
              <EditIcon />
            </EditButton>
            {shouldShowDeleteButton && (
              <RemoveButton
                onClick={showDeleteItemModal}
                disabled={isLanguageChanging || disableActions}>
                <DeleteIcon />
              </RemoveButton>
            )}
          </ActionIcons>
        </ParcelItemContainer>
      )}
      {isEditing && (
        <ParcelItemEditForm
          parcelItem={item}
          displayCancelButton
          currencySymbol={currencySymbol}
        />
      )}
      {withSeparator && <Separator mt='1.6rem' />}
    </ParcelItemWrapper>
  );
};

export default observer(ParcelItemComponent);
